/**
 * 97. Data Import Center — upload; mapping; validation; preview; dry run;
 * reconciliation history. Imports are transactional and never silently
 * corrupt data: DryRun writes nothing; Commit wraps in one transaction with
 * before/after reconciliation hashes.
 *
 * Arabic and English (C7): every word from the catalogs; the kind of record
 * imported and the job's status are words, never the stored value; counts
 * are numbers the locale writes.
 */
import { useId } from "react";
import {
  Card,
  PageHeader,
  Badge,
  DataTable,
  Skeleton,
  StatusBadge,
} from "@/components/ui";
import { PendingButton, PendingNote } from "@/components/PendingAction";
import { useApiList } from "@/lib/api/hooks";
import { useTranslation } from "@/lib/i18n";
import { listTitle } from "@/lib/i18n/format";
import { recordTypeLabel } from "@/lib/i18n/records";
import { formatDate, formatQuantity } from "@/lib/money";

interface ImportJob {
  id: string;
  entityType: string;
  status:
    | "Uploaded"
    | "Validating"
    | "Previewing"
    | "DryRun"
    | "Completed"
    | "Failed"
    | "RolledBack";
  totalRows: number;
  successCount: number;
  errorCount: number;
  startedAt: string | null;
}

export function DataImportCenterPage() {
  const { t } = useTranslation();
  const pendingId = useId();
  const { data, isLoading } = useApiList<ImportJob>(
    ["migration", "imports"],
    "/migration/imports",
  );

  return (
    <div>
      <PageHeader
        title={t("admin.dataImport.title")}
        subtitle={t("admin.dataImport.subtitle")}
        actions={
          <>
            <PendingButton reasonId={pendingId}>
              {t("admin.dataImport.template")}
            </PendingButton>
            <PendingButton variant="primary" reasonId={pendingId}>
              {t("admin.dataImport.new")}
            </PendingButton>
          </>
        }
      />
      {/* No route creates an import job yet, and the runner that would
          write one is broken (crm.md section 1; D103, D105): templates, new
          imports and opening a job all wait for the import slice. */}
      <PendingNote id={pendingId} reason="dataImport" slice="F14-W" />
      {isLoading || !data ? (
        <Skeleton rows={8} />
      ) : (
        <Card title={listTitle(t("admin.dataImport.list"), data.total)}>
          <DataTable<ImportJob>
            rows={data.items}
            columns={[
              {
                key: "entityType",
                label: t("fields.entity"),
                render: (r) =>
                  r.entityType ? recordTypeLabel(t, r.entityType) : "—",
              },
              {
                key: "status",
                label: t("fields.status"),
                render: (r) => (
                  <StatusBadge status={r.status} type="ImportJob" />
                ),
              },
              {
                key: "totalRows",
                label: t("admin.dataImport.rows"),
                numeric: true,
                render: (r) => formatQuantity(r.totalRows, 0),
              },
              {
                key: "successCount",
                label: t("admin.dataImport.success"),
                numeric: true,
                render: (r) => formatQuantity(r.successCount, 0),
              },
              {
                key: "errorCount",
                label: t("admin.dataImport.errors"),
                numeric: true,
                render: (r) =>
                  r.errorCount > 0 ? (
                    <Badge tone="danger">
                      {formatQuantity(r.errorCount, 0)}
                    </Badge>
                  ) : (
                    formatQuantity(r.errorCount, 0)
                  ),
              },
              {
                key: "startedAt",
                label: t("admin.dataImport.started"),
                render: (r) => formatDate(r.startedAt),
              },
              {
                key: "id",
                label: t("fields.actions"),
                render: () => (
                  <PendingButton size="sm" reasonId={pendingId}>
                    {t("admin.dataImport.open")}
                  </PendingButton>
                ),
              },
            ]}
          />
        </Card>
      )}
    </div>
  );
}
